import { Alert, AlertDescription, AlertTitle, Button } from '@tinct/web-components';
import { CheckIcon, CopyIcon, DownloadIcon, TriangleAlertIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type RecoveryCodesListProps = {
  codes: string[];
  /** Used to name the downloaded file. */
  accountEmail?: string | null;
};

const asText = (codes: string[], accountEmail?: string | null) =>
  [
    'Tinct recovery codes' + (accountEmail ? ` for ${accountEmail}` : ''),
    'Each code works once. Keep them somewhere safe, they will not be shown again.',
    '',
    ...codes,
    '',
  ].join('\n');

/**
 * The single-use recovery codes, shown exactly once after activation or regeneration.
 * Offers copy and download because the user has to store them outside Tinct now.
 */
export const RecoveryCodesList = ({ codes, accountEmail }: RecoveryCodesListProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      toast.success('Recovery codes copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not access the clipboard, select the codes and copy them by hand');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([asText(codes, accountEmail)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tinct-recovery-codes.txt';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-4">
      <Alert variant="important">
        <TriangleAlertIcon />
        <AlertTitle>Save these codes now, they are shown only once</AlertTitle>
        <AlertDescription>
          Each code signs you in once if you lose access to your authenticator app. Store them in a
          password manager or print them. Any codes you had before are no longer valid.
        </AlertDescription>
      </Alert>

      <ul
        aria-label="Recovery codes"
        className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg border bg-muted/40 px-5 py-4 font-mono text-sm select-all"
      >
        {codes.map((code) => (
          <li key={code} className="tracking-wider">
            {code}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
          {copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
          Copy codes
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={handleDownload}>
          <DownloadIcon className="size-4" />
          Download .txt
        </Button>
      </div>
    </div>
  );
};
